import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Linkedin, Send } from "lucide-react";
import { SITE, NAV_LINKS } from "../lib/site";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative bg-ink text-paper" data-testid="site-footer">
      <div className="h-1 w-full bg-voltage" />

      <div className="max-w-site mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-20 pb-10">
        <div className="grid grid-cols-12 gap-10">
          {/* Brand block */}
          <div className="col-span-12 md:col-span-5">
            <div className="font-display text-[26px] md:text-[32px] leading-[1.05] tracking-tight">
              {SITE.name}
            </div>
            <div className="mt-2 text-[10px] tracking-[0.18em] uppercase text-paper/55">
              {SITE.descriptor} · Coimbatore, India
            </div>
            <p className="mt-5 max-w-md text-paper/85 leading-relaxed text-[14px]">
              Audit, tax, compliance, and advisory&mdash;handled end-to-end for 1,000+ clients across India.
            </p>
            <div className="mt-6">
              <a
                href={SITE.arattaiUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-arattai"
                className="inline-flex items-center gap-2 text-[13px] font-medium px-3 py-2 rounded-md border border-white/20 text-paper/85 hover:border-voltage hover:text-voltage transition-colors"
              >
                <Send size={14} strokeWidth={1.8} />
                <span>Follow us on Arattai</span>
              </a>
            </div>
          </div>

          {/* Navigate */}
          <div className="col-span-6 md:col-span-3">
            <div className="text-[10px] tracking-[0.18em] uppercase text-paper/55 mb-4">
              Navigate
            </div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="text-[14px] text-paper/85 hover:text-voltage transition-colors link-wipe"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/representation-and-dispute"
                  data-testid="footer-link-rep"
                  className="text-[14px] text-paper/85 hover:text-voltage transition-colors link-wipe"
                >
                  Representation
                </Link>
              </li>
              <li>
                <Link
                  to="/msme-recovery"
                  data-testid="footer-link-msme"
                  className="text-[14px] text-paper/85 hover:text-voltage transition-colors link-wipe"
                >
                  MSME Recovery
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-12 md:col-span-4">
            <div className="text-[10px] tracking-[0.18em] uppercase text-paper/55 mb-4">
              Contact
            </div>
            <ul className="space-y-3.5 text-[14px]">
              <li className="flex items-start gap-3">
                <MapPin size={16} strokeWidth={1.6} className="mt-1 shrink-0 text-voltage" />
                <span className="text-paper/85 leading-relaxed">
                  {SITE.address.line1}
                  <br />
                  {SITE.address.line2}
                  <br />
                  {SITE.address.line3}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} strokeWidth={1.6} className="text-voltage" />
                <span data-testid="footer-phone" className="text-paper/85 select-all">
                  {SITE.phoneDisplay}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} strokeWidth={1.6} className="text-voltage" />
                <span data-testid="footer-email" className="text-paper/85 select-all break-all">
                  {SITE.email}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-5 border-t border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-4 text-[11px] text-paper/55">
          <div>&copy; {year} {SITE.name}. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <a
              href="https://www.linkedin.com/in/sri-vaibhav/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="hover:text-voltage transition-colors"
            >
              <Linkedin size={16} strokeWidth={1.6} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
